import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import StockModelCell from "components/Common/stock-table/StockModelCell"
import BillInfoCell from "components/Common/EmployeeBill/BillInfoCell"
import BulkBillPaymentModal from "components/Common/EmployeeBill/BulkBillPaymentModal"
import EmployeeBillFormModal from "components/Common/EmployeeBill/EmployeeBillFormModal"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"
import { useEmployees } from "hooks/employeeHook"
import {
  usePettyCashPaymentFroms,
  usePettyCashPaymentModes,
  usePettyCashPaymentPurposes,
} from "hooks/usePettyCashMaster"
import {
  useAdjustEmployeeWallet,
  useCreateEmployeeBill,
  useCreateMyEmployeeBill,
  useDeleteEmployeeBill,
  useEditEmployeeBill,
  useEmployeeBills,
  useEmployeeWallets,
  useMyEmployeeBills,
  useMyEmployeeWallet,
  usePayEmployeeAdvance,
  usePayEmployeeBill,
  usePayEmployeeBillBulk,
  useRejectEmployeeBill,
} from "hooks/useEmployeeBill"
import { useListParams } from "hooks/useListParams"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"

const emptyPayment = {
  employee_bill_id: "",
  employee_id: "",
  paid_amount: "",
  payment_date: "",
  payment_from_id: "",
  payment_mode_id: "",
  payment_for_id: "",
  notes: "",
}
const emptyAdjustment = { employee_id: "", adjustment_type: "increase_due", amount: "", notes: "" }

const statusColor = status => {
  if (status === "paid") return "success"
  if (status === "partially_paid") return "warning"
  if (status === "cancelled") return "secondary"
  if (status === "rejected") return "danger"
  return "info"
}

const listFrom = response => response && response.data && response.data.data || response && response.data || []
const canPayBill = bill => ["pending", "partially_paid"].includes(bill && bill.status)
const canRejectBill = bill => !["paid", "cancelled", "rejected"].includes(bill && bill.status)
const canEditBill = bill => {
  if (["paid", "cancelled", "rejected", "partially_paid"].includes(bill && bill.status)) return false
  if (Number(bill && bill.paid_amount || 0) > 0 || Number(bill && bill.adjusted_amount || 0) > 0) return false
  return true
}

const vehicleLabel = vehicle => {
  if (!vehicle) return null
  const name = [vehicle.brand && vehicle.brand.name, vehicle.model && vehicle.model.name].filter(Boolean).join(" ")
  const color = vehicle.exterior_color && vehicle.exterior_color.name
  const parts = [
    name || null,
    color || null,
    vehicle.stock_id ? `ID: ${vehicle.stock_id}` : null,
    vehicle.full_chassis ? `Chassis: ${vehicle.full_chassis}` : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(" · ") : null
}

const vehicleSentence = bill => {
  const items = Array.isArray(bill && bill.items) ? bill.items : []
  const vehicles = items.map(item => item.vehicle).filter(Boolean)
  const list = vehicles.length > 0 ? vehicles : [bill && bill.vehicle].filter(Boolean)
  const labels = list.map(vehicleLabel).filter(Boolean)
  return labels.length > 0 ? labels.join(", ") : "-"
}

const EmployeeBillList = ({ mine = false }) => {
  document.title = mine ? "My Employee Bills" : "Employee Bills"

  const [billModal, setBillModal] = useState(false)
  const [paymentModal, setPaymentModal] = useState(false)
  const [advanceModal, setAdvanceModal] = useState(false)
  const [bulkModal, setBulkModal] = useState(false)
  const [bulkEmployeeId, setBulkEmployeeId] = useState("")
  const [adjustModal, setAdjustModal] = useState(false)
  const [viewModal, setViewModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [paymentForm, setPaymentForm] = useState(emptyPayment)
  const [adjustForm, setAdjustForm] = useState(emptyAdjustment)
  const [rejectNotes, setRejectNotes] = useState("")
  const [selectedBill, setSelectedBill] = useState(null)
  const [editingBill, setEditingBill] = useState(null)

  const { params, setPage, setSearch } = useListParams({ page: 1, search: "" })
  const queryParams = useMemo(() => ({ page: params.page || 1, per_page: params.per_page || 10, search: params.search || "" }), [params])

  const adminBillsQuery = useEmployeeBills(queryParams, !mine)
  const myBillsQuery = useMyEmployeeBills(queryParams, mine)
  const adminWalletQuery = useEmployeeWallets({ page: 1, per_page: 50 }, !mine)
  const myWalletQuery = useMyEmployeeWallet(mine)
  const { data: paymentPurposeResponse } = usePettyCashPaymentPurposes({ all: true, per_page: 200 })
  const { data: paymentFromResponse } = usePettyCashPaymentFroms({ all: true, per_page: 200 }, !mine)
  const { data: paymentModeResponse } = usePettyCashPaymentModes({ all: true, per_page: 200 }, !mine)
  const { data: employeeResponse } = useEmployees({ all: true, status: "active", per_page: 500 }, !mine)

  const createAdminBill = useCreateEmployeeBill()
  const createOwnBill = useCreateMyEmployeeBill()
  const { mutateAsync: editBill, isPending: editingBillPending } = useEditEmployeeBill()
  const { mutateAsync: payBill, isPending: payingBill } = usePayEmployeeBill()
  const { mutateAsync: payAdvance, isPending: payingAdvance } = usePayEmployeeAdvance()
  const { mutateAsync: payBulk, isPending: payingBulk } = usePayEmployeeBillBulk()
  const { mutateAsync: adjustWallet, isPending: adjustingWallet } = useAdjustEmployeeWallet()
  const { mutateAsync: deleteBill, isPending: deletingBill } = useDeleteEmployeeBill()
  const { mutateAsync: rejectBill, isPending: rejectingBill } = useRejectEmployeeBill()

  const billsQuery = mine ? myBillsQuery : adminBillsQuery
  const walletQuery = mine ? myWalletQuery : adminWalletQuery
  const createBill = mine ? createOwnBill : createAdminBill
  const bills = listFrom(billsQuery.data)
  const wallets = mine ? [myWalletQuery.data && myWalletQuery.data.data].filter(Boolean) : listFrom(adminWalletQuery.data)
  const paymentPurposes = listFrom(paymentPurposeResponse)
  const paymentFroms = listFrom(paymentFromResponse)
  const paymentModes = listFrom(paymentModeResponse)
  const employees = listFrom(employeeResponse)
  const paginationInfo = billsQuery.data && billsQuery.data.data

  const walletSummary = useMemo(() => {
    if (mine) return walletQuery.data && walletQuery.data.data || {}
    return wallets.reduce((sum, wallet) => ({
      due_amount: sum.due_amount + Number(wallet && wallet.due_amount || 0),
      overpaid_amount: sum.overpaid_amount + Number(wallet && wallet.overpaid_amount || 0),
      net_balance: sum.net_balance + Number(wallet && wallet.net_balance || 0),
    }), { due_amount: 0, overpaid_amount: 0, net_balance: 0 })
  }, [mine, walletQuery.data, wallets])

  const setPaymentValue = (key, value) => setPaymentForm(prev => Object.assign({}, prev, { [key]: value }))
  const setAdjustValue = (key, value) => setAdjustForm(prev => Object.assign({}, prev, { [key]: value }))

  const openBillModal = bill => {
    setEditingBill(bill || null)
    setBillModal(true)
  }

  const closeBillModal = () => {
    setBillModal(false)
    setEditingBill(null)
  }

  const openPaymentModal = bill => {
    setSelectedBill(bill)
    setPaymentForm(Object.assign({}, emptyPayment, { employee_bill_id: bill.id, employee_id: bill.employee_id }))
    setPaymentModal(true)
  }

  const openAdvanceModal = (employeeId = "") => {
    setPaymentForm(Object.assign({}, emptyPayment, { employee_id: employeeId }))
    setAdvanceModal(true)
  }

  const openBulkModal = (employeeId = "") => {
    setBulkEmployeeId(employeeId ? String(employeeId) : "")
    setBulkModal(true)
  }

  const refreshBillsAndWallet = () => {
    billsQuery.refetch()
    if (walletQuery.refetch) walletQuery.refetch()
  }

  const submitBulkPayment = payload =>
    payBulk(payload)
      .then(response => {
        toast.success("Bulk bill payment recorded.")
        return response
      })
      .catch(error => {
        toast.error("Failed to record bulk payment.")
        throw error
      })

  const openAdjustModal = (employeeId = "") => {
    setAdjustForm(Object.assign({}, emptyAdjustment, { employee_id: employeeId }))
    setAdjustModal(true)
  }

  const openViewModal = bill => {
    setSelectedBill(bill)
    setViewModal(true)
  }

  const openRejectModal = bill => {
    setSelectedBill(bill)
    setRejectNotes("")
    setRejectModal(true)
  }

  function submitBill(payload) {
    if (editingBill) {
      editBill({
          id: editingBill.id,
          employee_id: editingBill.employee_id,
          notes: payload.notes,
          amount: payload.amount,
          payment_for_id: payload.payment_for_id,
          items: payload.items,
        })
        .then(() => {
        toast.success("Employee bill updated.")
          closeBillModal()
          billsQuery.refetch()
          if (walletQuery.refetch) walletQuery.refetch()
        })
        .catch(() => {
          toast.error("Failed to update employee bill.")
        })
      return
    }

    createBill.mutateAsync({
          employee_id: mine ? undefined : payload.employee_id,
          notes: payload.notes,
          amount: payload.amount,
          payment_for_id: payload.payment_for_id,
          items: payload.items,
        })
      .then(() => {
        toast.success("Employee bill request saved.")
        closeBillModal()
        billsQuery.refetch()
        if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to save employee bill.")
      })
  }

  function submitPayment() {
    payBill({
        employee_bill_id: paymentForm.employee_bill_id,
        paid_amount: paymentForm.paid_amount,
        payment_date: paymentForm.payment_date || undefined,
        payment_from_id: paymentForm.payment_from_id,
        payment_mode_id: paymentForm.payment_mode_id,
        notes: paymentForm.notes,
      })
      .then(() => {
      toast.success("Employee bill payment recorded.")
      setPaymentModal(false)
      billsQuery.refetch()
      if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to record payment.")
      })
  }

  function submitAdvance() {
    payAdvance({
        employeeId: paymentForm.employee_id,
        paid_amount: paymentForm.paid_amount,
        payment_date: paymentForm.payment_date || undefined,
        payment_from_id: paymentForm.payment_from_id,
        payment_mode_id: paymentForm.payment_mode_id,
        payment_for_id: paymentForm.payment_for_id,
        notes: paymentForm.notes,
      })
      .then(() => {
      toast.success("Employee advance recorded.")
      setAdvanceModal(false)
      if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to record advance.")
      })
  }

  function submitAdjustment() {
    adjustWallet({
        employeeId: adjustForm.employee_id,
        adjustment_type: adjustForm.adjustment_type,
        amount: adjustForm.amount,
        notes: adjustForm.notes,
      })
      .then(() => {
      toast.success("Employee wallet adjusted.")
      setAdjustModal(false)
      if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to adjust wallet.")
      })
  }

  function confirmDelete() {
    deleteBill(selectedBill.id)
      .then(() => {
      toast.success("Employee bill deleted.")
      setDeleteModal(false)
      billsQuery.refetch()
      if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to delete employee bill.")
      })
  }

  function confirmReject() {
    rejectBill({ id: selectedBill.id, notes: rejectNotes })
      .then(() => {
      toast.success("Employee bill rejected.")
      setRejectModal(false)
      billsQuery.refetch()
      if (walletQuery.refetch) walletQuery.refetch()
      })
      .catch(() => {
        toast.error("Failed to reject employee bill.")
      })
  }

  const columns = useMemo(
    () => [
      {
        header: "Vehicle",
        accessorKey: "vehicle_info",
        cell: ({ row }) => (
          <span className="text-muted small" style={{ maxWidth: 280, display: "inline-block" }}>
            {vehicleSentence(row.original)}
          </span>
        ),
      },
      {
        header: "Bill Info",
        accessorKey: "bill_info",
        cell: ({ row }) => <BillInfoCell bill={row.original} />,
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const bill = row.original
          const payable = canPayBill(bill)
          const payTitle = payable ? "Pay Bill" : bill && bill.status === "paid" ? "Bill already paid" : "Bill is not payable"
          return (
            <div className="d-flex gap-1 flex-wrap">
              <Button color="info" size="sm" onClick={() => openViewModal(bill)} title="View Details">
                <i className="mdi mdi-eye-outline" />
              </Button>
              {!mine && canEditBill(bill) && (
                <CanView access={[ACCESS.EMPLOYEE_BILL.EDIT]}>
                  <Button color="primary" size="sm" onClick={() => openBillModal(bill)} title="Edit Bill">
                    <i className="mdi mdi-pencil-outline" />
                  </Button>
                </CanView>
              )}
              <CanView access={[ACCESS.EMPLOYEE_BILL_PAYMENT.ADD]}>
                <Button color="success" size="sm" disabled={!payable} onClick={() => payable && openPaymentModal(bill)} title={payTitle}>
                  <i className="mdi mdi-cash-plus" />
                </Button>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_WALLET.ADD_ADVANCE]}>
                <Button color="secondary" size="sm" onClick={() => openAdvanceModal(bill.employee_id)} title="Pay Advance">
                  <i className="mdi mdi-wallet-plus-outline" />
                </Button>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_BILL_PAYMENT.ADD]}>
                <Button color="success" size="sm" outline onClick={() => openBulkModal(bill.employee_id)} title="Bulk Pay Bills">
                  <i className="mdi mdi-cash-multiple" />
                </Button>
              </CanView>
              {!mine && canRejectBill(bill) && (
                <CanView access={[ACCESS.EMPLOYEE_BILL.EDIT]}>
                  <Button color="warning" size="sm" onClick={() => openRejectModal(bill)} title="Reject">
                    <i className="mdi mdi-close-octagon-outline" />
                  </Button>
                </CanView>
              )}
              <CanView access={[ACCESS.EMPLOYEE_BILL.DELETE]}>
                <Button color="danger" size="sm" onClick={() => { setSelectedBill(bill); setDeleteModal(true) }} title="Delete">
                  <i className="mdi mdi-delete-outline" />
                </Button>
              </CanView>
            </div>
          )
        },
      },
    ],
    []
  )

  return (
    <PageWrapper title={mine ? "My Employee Bills" : "Employee Bills"} breadcrumbItem={mine ? "My Bills" : "Employee Bill List"}>
      <Row className="g-3 mb-3">
        <Col md={4}>
          <div className="border rounded p-3 h-100">
            <div className="text-muted small">Due Amount</div>
            <div className="fs-5 fw-semibold">{formatCurrency(walletSummary && walletSummary.due_amount || 0, "BDT")}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="border rounded p-3 h-100">
            <div className="text-muted small">Overpaid / Advance</div>
            <div className="fs-5 fw-semibold">{formatCurrency(walletSummary && walletSummary.overpaid_amount || 0, "BDT")}</div>
          </div>
        </Col>
        <Col md={4}>
          <div className="border rounded p-3 h-100">
            <div className="text-muted small">Net Balance</div>
            <div className="fs-5 fw-semibold">{formatCurrency(walletSummary && walletSummary.net_balance || 0, "BDT")}</div>
          </div>
        </Col>
      </Row>

      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <Input className="w-auto" placeholder="Search employee" value={params.search || ""} onChange={e => setSearch(e.target.value)} />
        <div className="d-flex gap-2">
          <CanView access={mine ? [ACCESS.EMPLOYEE_BILL.ADD_OWN] : [ACCESS.EMPLOYEE_BILL.ADD]}>
            <Button color="primary" onClick={() => openBillModal()}><i className="mdi mdi-plus me-1" />Request Bill</Button>
          </CanView>
          {!mine && (
            <>
              <CanView access={[ACCESS.EMPLOYEE_WALLET.ADD_ADVANCE]}>
                <Button color="secondary" onClick={() => openAdvanceModal()}><i className="mdi mdi-wallet-plus-outline me-1" />Pay Advance</Button>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_BILL_PAYMENT.ADD]}>
                <Button color="success" onClick={() => openBulkModal()}><i className="mdi mdi-cash-multiple me-1" />Bulk Pay</Button>
              </CanView>
              <CanView access={[ACCESS.EMPLOYEE_WALLET.ADJUST]}>
                <Button color="warning" onClick={() => openAdjustModal()}><i className="mdi mdi-tune me-1" />Adjust Wallet</Button>
              </CanView>
            </>
          )}
        </div>
      </div>

      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} centered size="lg">
        <ModalHeader toggle={() => setViewModal(false)}>Bill Details #{selectedBill && selectedBill.id}</ModalHeader>
        <ModalBody>
          {selectedBill && (
            <>
              <Row className="g-3 mb-4">
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Employee</Label>
                  <div>{selectedBill.employee && selectedBill.employee.name || "-"}</div>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Purpose</Label>
                  <div>{selectedBill.payment_for && selectedBill.payment_for.name || selectedBill.expense_type && selectedBill.expense_type.name || "-"}</div>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Amount</Label>
                  <div className="fs-6 fw-semibold">{formatCurrency(selectedBill.amount, "BDT")}</div>
                </Col>
                {Array.isArray(selectedBill.items) && selectedBill.items.length > 0 && (
                  <Col xs={12}>
                    <Label className="text-muted small">Bill Items</Label>
                    <Table responsive borderless className="mb-0">
                      <thead>
                        <tr className="text-muted small">
                          <th>Purpose</th>
                          <th>Vehicle</th>
                          <th>Amount</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedBill.items.map(item => (
                          <tr key={item.id || `${item.payment_for_id}-${item.amount}`}>
                            <td>{item.payment_for && item.payment_for.name || item.expense_type && item.expense_type.name || "-"}</td>
                            <td>{[item.vehicle && item.vehicle.stock_id, item.vehicle && item.vehicle.full_chassis].filter(Boolean).join(" / ") || "-"}</td>
                            <td>{formatCurrency(item.amount || 0, "BDT")}</td>
                            <td>{item.notes || item.description || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                )}
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Status</Label>
                  <Badge color={statusColor(selectedBill.status)}>{selectedBill.status ? String(selectedBill.status).split("_").join(" ") : "pending"}</Badge>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Paid</Label>
                  <div>{formatCurrency(selectedBill.paid_amount, "BDT")}</div>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Adjusted</Label>
                  <div>{formatCurrency(selectedBill.adjusted_amount, "BDT")}</div>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Remaining</Label>
                  <div className="fw-semibold">{formatCurrency(selectedBill.remaining_amount != null ? selectedBill.remaining_amount : Math.max(0, selectedBill.amount - selectedBill.paid_amount - selectedBill.adjusted_amount), "BDT")}</div>
                </Col>
                <Col xs={12} sm={6}>
                  <Label className="text-muted small">Created</Label>
                  <div>{formatReadableDate(selectedBill.created_at)}</div>
                </Col>
                <Col xs={12}>
                  <Label className="text-muted small">Remarks</Label>
                  <div>{selectedBill.notes || "-"}</div>
                </Col>
                {(!Array.isArray(selectedBill.items) || !selectedBill.items.some(item => item.vehicle_id)) && (
                  <Col xs={12}>
                    <Label className="text-muted small">Vehicle</Label>
                    <StockModelCell
                      stock={selectedBill.vehicle || {}}
                      isMobile={false}
                      isExpanded={() => false}
                      toggleRow={() => {}}
                      rowId={selectedBill && selectedBill.id}
                      showIdentifiers
                      showImage
                    />
                  </Col>
                )}
              </Row>

              {selectedBill.payments && selectedBill.payments.length > 0 && (
                <>
                  <Label className="text-muted small">Payment History</Label>
                  <Table responsive borderless className="mb-3">
                    <thead>
                      <tr className="text-muted small">
                        <th>Date</th>
                        <th>Amount</th>
                        <th>From</th>
                        <th>Mode</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBill.payments.map(payment => (
                        <tr key={payment.id}>
                          <td>{payment.payment_date ? formatReadableDate(payment.payment_date) : "-"}</td>
                          <td>{formatCurrency(payment.paid_amount, "BDT")}</td>
                          <td>{payment.payment_from && payment.payment_from.name || "-"}</td>
                          <td>{payment.payment_mode && payment.payment_mode.name || "-"}</td>
                          <td>{payment.notes || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </>
          )}
        </ModalBody>
      </Modal>

      <DeleteModal show={deleteModal} loading={deletingBill} onDeleteClick={confirmDelete} onCloseClick={() => setDeleteModal(false)} />

      <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)} centered>
        <ModalHeader toggle={() => setRejectModal(false)}>Reject Employee Bill</ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">Reject bill #{selectedBill && selectedBill.id} for {formatCurrency(selectedBill && selectedBill.amount || 0, "BDT")}.</p>
          <Label>Reason</Label>
          <Input type="textarea" value={rejectNotes} onChange={e => setRejectNotes(e.target.value)} />
          <div className="text-end mt-3">
            <Button color="warning" disabled={rejectingBill} onClick={confirmReject}>
              {rejectingBill ? "Rejecting..." : "Reject Bill"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <TableContainer
        columns={columns}
        data={bills}
        loading={billsQuery.isLoading}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
      />

      <EmployeeBillFormModal
        isOpen={billModal}
        onClose={closeBillModal}
        onSubmit={submitBill}
        loading={createBill.isPending || editingBillPending}
        editingBill={editingBill}
        mine={mine}
        employees={employees}
        paymentPurposes={paymentPurposes}
      />

      <Modal isOpen={paymentModal} toggle={() => setPaymentModal(false)} centered>
        <ModalHeader toggle={() => setPaymentModal(false)}>Record Bill Payment</ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">Bill #{selectedBill && selectedBill.id} remaining: {formatCurrency(selectedBill && selectedBill.remaining_amount != null ? selectedBill.remaining_amount : Math.max(0, Number(selectedBill && selectedBill.amount || 0) - Number(selectedBill && selectedBill.paid_amount || 0) - Number(selectedBill && selectedBill.adjusted_amount || 0)), "BDT")}</p>
          <Label>Paid Amount</Label>
          <Input type="number" min="0" step="0.01" value={paymentForm.paid_amount} onChange={e => setPaymentValue("paid_amount", e.target.value)} />
          <Label className="mt-3">Payment Date</Label>
          <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentValue("payment_date", e.target.value)} />
          <Label className="mt-3">Payment From</Label>
          <Input type="select" value={paymentForm.payment_from_id} onChange={e => setPaymentValue("payment_from_id", e.target.value)}>
            <option value="">Select payment source</option>
            {paymentFroms.map(source => <option key={source.id} value={source.id}>{source.name}</option>)}
          </Input>
          <Label className="mt-3">Payment Mode</Label>
          <Input type="select" value={paymentForm.payment_mode_id} onChange={e => setPaymentValue("payment_mode_id", e.target.value)}>
            <option value="">Select payment mode</option>
            {paymentModes.map(mode => <option key={mode.id} value={mode.id}>{mode.name}</option>)}
          </Input>
          <Label className="mt-3">Notes</Label>
          <Input type="textarea" value={paymentForm.notes} onChange={e => setPaymentValue("notes", e.target.value)} />
          <div className="text-end mt-3"><Button color="success" disabled={payingBill} onClick={submitPayment}>Record Payment</Button></div>
        </ModalBody>
      </Modal>

      <Modal isOpen={advanceModal} toggle={() => setAdvanceModal(false)} centered>
        <ModalHeader toggle={() => setAdvanceModal(false)}>Pay Employee Advance</ModalHeader>
        <ModalBody>
          <Label>Employee</Label>
          <Input type="select" value={paymentForm.employee_id} onChange={e => setPaymentValue("employee_id", e.target.value)}>
            <option value="">Select employee</option>
            {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
          </Input>
          <Label className="mt-3">Paid Amount</Label>
          <Input type="number" min="0" step="0.01" value={paymentForm.paid_amount} onChange={e => setPaymentValue("paid_amount", e.target.value)} />
          <Label className="mt-3">Payment Date</Label>
          <Input type="date" value={paymentForm.payment_date} onChange={e => setPaymentValue("payment_date", e.target.value)} />
          <Label className="mt-3">Payment From</Label>
          <Input type="select" value={paymentForm.payment_from_id} onChange={e => setPaymentValue("payment_from_id", e.target.value)}>
            <option value="">Select payment source</option>
            {paymentFroms.map(source => <option key={source.id} value={source.id}>{source.name}</option>)}
          </Input>
          <Label className="mt-3">Payment Mode</Label>
          <Input type="select" value={paymentForm.payment_mode_id} onChange={e => setPaymentValue("payment_mode_id", e.target.value)}>
            <option value="">Select payment mode</option>
            {paymentModes.map(mode => <option key={mode.id} value={mode.id}>{mode.name}</option>)}
          </Input>
          <Label className="mt-3">Payment Purpose</Label>
          <Input type="select" value={paymentForm.payment_for_id} onChange={e => setPaymentValue("payment_for_id", e.target.value)}>
            <option value="">Select payment purpose</option>
            {paymentPurposes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
          </Input>
          <Label className="mt-3">Notes</Label>
          <Input type="textarea" value={paymentForm.notes} onChange={e => setPaymentValue("notes", e.target.value)} />
          <div className="text-end mt-3"><Button color="secondary" disabled={payingAdvance} onClick={submitAdvance}>Record Advance</Button></div>
        </ModalBody>
      </Modal>

      <Modal isOpen={adjustModal} toggle={() => setAdjustModal(false)} centered>
        <ModalHeader toggle={() => setAdjustModal(false)}>Adjust Employee Wallet</ModalHeader>
        <ModalBody>
          <Label>Employee</Label>
          <Input type="select" value={adjustForm.employee_id} onChange={e => setAdjustValue("employee_id", e.target.value)}>
            <option value="">Select employee</option>
            {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
          </Input>
          <Label className="mt-3">Adjustment Type</Label>
          <Input type="select" value={adjustForm.adjustment_type} onChange={e => setAdjustValue("adjustment_type", e.target.value)}>
            <option value="increase_due">Increase Due</option>
            <option value="decrease_due">Decrease Due</option>
            <option value="increase_overpaid">Increase Overpaid</option>
            <option value="decrease_overpaid">Decrease Overpaid</option>
          </Input>
          <Label className="mt-3">Amount</Label>
          <Input type="number" min="0" step="0.01" value={adjustForm.amount} onChange={e => setAdjustValue("amount", e.target.value)} />
          <Label className="mt-3">Reason</Label>
          <Input type="textarea" value={adjustForm.notes} onChange={e => setAdjustValue("notes", e.target.value)} />
          <div className="text-end mt-3"><Button color="warning" disabled={adjustingWallet} onClick={submitAdjustment}>Adjust</Button></div>
        </ModalBody>
      </Modal>

      <BulkBillPaymentModal
        isOpen={bulkModal}
        onClose={() => setBulkModal(false)}
        onSubmitted={refreshBillsAndWallet}
        loading={payingBulk}
        submit={submitBulkPayment}
        employees={employees}
        paymentFroms={paymentFroms}
        paymentModes={paymentModes}
        defaultEmployeeId={bulkEmployeeId}
      />
    </PageWrapper>
  )
}

export default EmployeeBillList
